import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, BookOpen, Wallet, HandCoins,
  LogOut, Landmark, ChevronRight, Home
} from 'lucide-react';
import { useAuth } from '../../App';

const MemberLayout = ({ children, title, subtitle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/member/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/member/passbook', label: 'Passbook', icon: BookOpen },
    { path: '/member/loans', label: 'My Loans', icon: Wallet },
    { path: '/member/contributions', label: 'Contributions', icon: HandCoins },
  ];

  return (
    <div className="min-h-screen bg-[#F4F7FB]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 w-64 h-screen bg-[#0A2540] text-white z-40">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Landmark className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Shri Bhavani
              </h1>
              <p className="text-xs text-gray-400">Member Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1">
          <ul className="space-y-1">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-[#2962FF] text-white shadow-lg shadow-[#2962FF]/20'
                        : 'text-gray-300 hover:text-white hover:bg-white/10'
                    }`
                  }
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#00C853] flex items-center justify-center font-semibold">
              {user?.name?.charAt(0) || 'M'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name || 'Member'}</p>
              <p className="text-xs text-gray-400">Member</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="member-logout-btn"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-300 hover:text-white hover:bg-white/10 w-full transition-all"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100">
          <div className="px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <NavLink to="/" className="text-[#425466] hover:text-[#0A2540] flex items-center gap-1">
                <Home className="w-4 h-4" />
              </NavLink>
              <ChevronRight className="w-4 h-4 text-gray-300" />
              <span className="text-[#0A2540] font-medium">{title}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#425466]">
                {new Date().toLocaleDateString('en-IN', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8 pb-20">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {title}
            </h1>
            {subtitle && <p className="text-[#425466] mt-1">{subtitle}</p>}
          </div>

          {children}
        </div>
        
        {/* Footer */}
        <footer className="fixed bottom-0 right-0 left-64 bg-white border-t border-gray-100 py-3 px-8 text-center z-30">
          <p className="text-sm text-[#425466]">
            Designed By: <span className="font-semibold text-[#0A2540]">Viral Boostup</span> | 
            <a href="tel:+919110278059" className="text-[#00C853] hover:underline ml-1">+91 9110278059</a>
          </p>
        </footer>
      </main>
    </div>
  );
};

export default MemberLayout;
